import { Plus, Trash2, X } from "lucide-react";
import { fmtInt } from "@/lib/utils";

export const splitPoints = (text) =>
  (text || "").split("\n").map((l) => l.replace(/^[\s•\-–]+/, "").trim()).filter(Boolean);

export const sumItems = (items) => items.reduce((a, it) => ({
  calories: a.calories + Number(it.calories || 0),
  protein: a.protein + Number(it.protein_g || 0),
  carbs: a.carbs + Number(it.carbs_g || 0),
  fat: a.fat + Number(it.fat_g || 0),
}), { calories: 0, protein: 0, carbs: 0, fat: 0 });

// Totals strip with the Calofay mark — identical on the home card and saved recipes.
export function MacroStrip({ items, en, testid }) {
  const t = sumItems(items);
  const g = en ? "g" : "غ";
  return (
    <div className="bg-white rounded-2xl shadow-card px-3 pt-3 pb-2 mb-3" dir="rtl" data-testid={testid}>
      <div className="grid grid-cols-4 gap-1">
        {[
          { key: "cal", icon: "🔥", label: en ? "Calories" : "السعرات", value: t.calories, unit: "", color: "text-neutral-900" },
          { key: "p", icon: "🥩", label: en ? "Protein" : "بروتين", value: t.protein, unit: g, color: "text-sky-500" },
          { key: "c", icon: "🌾", label: en ? "Carbs" : "كارب", value: t.carbs, unit: g, color: "text-emerald-500" },
          { key: "f", icon: "🥑", label: en ? "Fat" : "دهون", value: t.fat, unit: g, color: "text-amber-700" },
        ].map((m) => (
          <div key={m.key} className="flex flex-col items-center gap-0.5">
            <div className="text-sm leading-none">{m.icon}</div>
            <div className={`flex items-baseline justify-center gap-[1px] ${m.color}`} dir="rtl">
              <bdi className="num font-black text-sm">{fmtInt(m.value)}</bdi>
              {m.unit && <span className="text-[9px] font-semibold text-neutral-400">{m.unit}</span>}
            </div>
            <div className="text-[10px] text-neutral-500 leading-none">{m.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-1.5 border-t border-neutral-100 flex items-center justify-center gap-1.5">
        <img src="/calofay-mark.webp" alt="" width={16} height={16} className="w-4 h-4 rounded-[5px] select-none" draggable={false} decoding="async" />
        <span className="text-[10px] font-bold text-neutral-500 tracking-tight">{en ? "Calofay App" : "تطبيق كالوفاي"}</span>
      </div>
    </div>
  );
}

// Read-only ingredient card (optional delete).
export function ItemCard({ it, en, onDelete, testid }) {
  const g = en ? "g" : "غ";
  const u = it.unit === "ml" ? (en ? "ml" : "مل") : g;
  return (
    <div className="bg-white rounded-2xl shadow-card p-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-neutral-900 leading-tight">{it.name}</div>
          {it.quantity_g > 0 && (
            <div className="text-[10px] text-neutral-400 mt-0.5 num flex items-center gap-0.5" dir={en ? "ltr" : "rtl"}>
              <bdi>{fmtInt(it.quantity_g)}</bdi><span>{u}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="num font-black text-brand-500">{fmtInt(it.calories)}<span className="text-[10px] font-semibold text-neutral-400 mr-0.5">kcal</span></div>
          {onDelete && (
            <button onClick={onDelete} className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center" data-testid={testid ? `${testid}-delete` : undefined}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap" dir={en ? "ltr" : "rtl"}>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 num font-semibold flex items-center gap-1">🥩 <bdi>{fmtInt(it.protein_g)}</bdi><span>{g}</span></span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 num font-semibold flex items-center gap-1">🌾 <bdi>{fmtInt(it.carbs_g)}</bdi><span>{g}</span></span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 num font-semibold flex items-center gap-1">🥑 <bdi>{fmtInt(it.fat_g)}</bdi><span>{g}</span></span>
      </div>
      {it.assumption && (
        <div className={`mt-2 text-[11px] text-neutral-600 leading-relaxed ${en ? "text-left" : "text-right"} bg-amber-50/70 rounded-lg p-2`}>
          <span className="font-semibold text-neutral-700">{en ? "Assumptions: " : "افتراضات: "}</span>{it.assumption}
        </div>
      )}
    </div>
  );
}

// Notes as bullet points. `value` is newline-joined text.
export function NotesPoints({ value, onChange, en, testid = "notes" }) {
  const pts = (value || "").split("\n");
  const list = pts.length ? pts : [""];
  const set = (arr) => onChange(arr.join("\n"));
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {list.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-brand-500 font-black">•</span>
          <input
            value={p.replace(/^[\s•\-–]+/, "")}
            onChange={(e) => set(list.map((x, j) => (j === i ? e.target.value : x)))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); set([...list.slice(0, i + 1), "", ...list.slice(i + 1)]); } }}
            placeholder={en ? "Write a note or step…" : "اكتب ملاحظة أو خطوة…"}
            className="flex-1 min-w-0 bg-neutral-50 border border-neutral-100 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
            data-testid={`${testid}-point-${i}`}
          />
          {list.length > 1 && (
            <button type="button" onClick={() => set(list.filter((_, j) => j !== i))} className="w-7 h-7 rounded-full text-neutral-400 hover:text-red-500 flex items-center justify-center" data-testid={`${testid}-point-remove-${i}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => set([...list, ""])} className="text-xs text-brand-500 font-semibold flex items-center gap-1 pt-1" data-testid={`${testid}-add-point`}>
        <Plus className="w-3.5 h-3.5" /> {en ? "Add point" : "إضافة نقطة"}
      </button>
    </div>
  );
}

export function NotesBullets({ text, testid }) {
  const pts = splitPoints(text);
  if (!pts.length) return null;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {pts.map((p, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-neutral-700 leading-relaxed">
          <span className="text-brand-500 font-black leading-relaxed">•</span><span className="flex-1">{p}</span>
        </li>
      ))}
    </ul>
  );
}
